import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Zap, Save, TestTube, Loader2, CheckCircle, XCircle, Users, Search, TrendingUp, BarChart3 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AIServiceConfigPage = () => {
  const { toast } = useToast();
  const [config, setConfig] = useState({
    service_url: '',
    api_key: '',
    api_key_masked: '',
    enabled: false,
    daily_limit_per_user: 10,
  });
  const [usage, setUsage] = useState(null);
  const [askAnalytics, setAskAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchConfig();
    fetchUsage();
    fetchAskAnalytics();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/admin-tools/ai-service/config`, { headers });
      setConfig(res.data);
    } catch (err) {
      console.error('Failed to fetch AI config:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/api/admin-tools/ai-service/usage`, { headers });
      setUsage(res.data);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const fetchAskAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/api/admin-tools/ai-service/ask-analytics`, { headers });
      setAskAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch ask analytics:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        service_url: config.service_url,
        enabled: config.enabled,
        daily_limit_per_user: config.daily_limit_per_user,
      };
      if (newApiKey) {
        payload.api_key = newApiKey;
      }
      await axios.put(`${API}/api/admin-tools/ai-service/config`, payload, { headers });
      toast({ title: "Saved", description: "AI service configuration updated" });
      setNewApiKey('');
      fetchConfig();
    } catch (err) {
      toast({ title: "Error", description: "Failed to save configuration", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/api/admin-tools/ai-service/test`, {}, { headers });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: 'Failed to test connection' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition max-w-3xl mx-auto" data-testid="ai-service-config-page">
        <Card className="mb-6 border-l-4 border-l-violet-600 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-violet-600/5 to-purple-600/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-violet-600 rounded-lg">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl sm:text-3xl text-violet-700" data-testid="config-title">
                  AI Service Connection
                </CardTitle>
                <CardDescription className="text-sm mt-1">
                  Configure the central AI service for Deep Search (RAG-powered answers)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-6">
          {/* Connection Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Connection Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Service URL</label>
                <Input
                  value={config.service_url}
                  onChange={(e) => setConfig({ ...config, service_url: e.target.value })}
                  placeholder="https://ai.yourdomain.com/api/v1"
                  data-testid="service-url-input"
                />
                <p className="text-xs text-gray-400 mt-1">The base URL of your central AI service</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">API Key</label>
                {config.api_key_masked && !newApiKey && (
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-mono text-xs">
                      {config.api_key_masked}
                    </Badge>
                    <button
                      onClick={() => setNewApiKey(' ')}
                      className="text-xs text-violet-600 hover:text-violet-800"
                    >
                      Change key
                    </button>
                  </div>
                )}
                {(!config.api_key_masked || newApiKey) && (
                  <Input
                    type="password"
                    value={newApiKey.trim()}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    placeholder="Enter API key from your central AI service"
                    data-testid="api-key-input"
                  />
                )}
                <p className="text-xs text-gray-400 mt-1">The API key issued by your central AI service</p>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Deep Search</label>
                  <p className="text-xs text-gray-400">When enabled, users can use "Find Deeper" to query the AI service</p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
                  data-testid="enable-toggle"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Daily Limit Per User</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={config.daily_limit_per_user}
                  onChange={(e) => setConfig({ ...config, daily_limit_per_user: parseInt(e.target.value) || 10 })}
                  className="w-32"
                  data-testid="daily-limit-input"
                />
                <p className="text-xs text-gray-400 mt-1">Maximum deep searches per user per day</p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="save-config-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Configuration
                </Button>
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={testing || !config.service_url}
                  data-testid="test-connection-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Test Connection
                </Button>
              </div>

              {testResult && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`} data-testid="test-result">
                  {testResult.success ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {testResult.message}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-500" />
                Deep Search Usage (Today)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {usage ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-violet-600" data-testid="today-total">
                      {usage.today_total}
                    </span>
                    <span className="text-sm text-gray-500">deep searches today</span>
                  </div>
                  {usage.top_users_today?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Top Users</p>
                      <div className="space-y-1">
                        {usage.top_users_today.map((u, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">
                              {u.name}
                              {u.indos_number && <span className="text-gray-400 ml-1">({u.indos_number})</span>}
                            </span>
                            <span className="text-gray-400">{u.count} searches</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {usage.top_users_today?.length === 0 && (
                    <p className="text-sm text-gray-400">No deep searches today</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Loading usage stats...</p>
              )}
            </CardContent>
          </Card>

          {/* Ask Search Analytics */}
          <Card data-testid="ask-analytics-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="w-5 h-5 text-violet-500" />
                Ask Search Analytics
              </CardTitle>
              <CardDescription>Semantic search usage patterns across the question bank</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {askAnalytics ? (
                <>
                  {/* Summary stats */}
                  <div className="grid grid-cols-3 gap-4" data-testid="ask-analytics-summary">
                    <div className="bg-violet-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-violet-700" data-testid="ask-today-count">{askAnalytics.today_total}</p>
                      <p className="text-xs text-violet-500 mt-0.5">Today</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700" data-testid="ask-week-count">{askAnalytics.week_total}</p>
                      <p className="text-xs text-blue-500 mt-0.5">This Week</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-700" data-testid="ask-alltime-count">{askAnalytics.all_time_total}</p>
                      <p className="text-xs text-gray-500 mt-0.5">All Time</p>
                    </div>
                  </div>

                  {/* Daily volume bar chart */}
                  {askAnalytics.daily_volume?.length > 0 && (
                    <div data-testid="ask-daily-volume">
                      <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="w-4 h-4 text-gray-500" />
                        <p className="text-sm font-medium text-gray-700">Daily Volume (Last 7 Days)</p>
                      </div>
                      <div className="flex items-end gap-1.5 h-24">
                        {askAnalytics.daily_volume.map((d) => {
                          const maxCount = Math.max(...askAnalytics.daily_volume.map(v => v.count), 1);
                          const heightPct = Math.max((d.count / maxCount) * 100, 4);
                          return (
                            <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-[10px] text-gray-500 font-medium">{d.count}</span>
                              <div
                                className="w-full bg-violet-400 rounded-t-sm transition-all"
                                style={{ height: `${heightPct}%` }}
                                title={`${d.date}: ${d.count} searches`}
                              />
                              <span className="text-[10px] text-gray-400">
                                {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Top queries */}
                  <div data-testid="ask-top-queries">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-700">Popular Queries (Last 7 Days)</p>
                    </div>
                    {askAnalytics.top_queries?.length > 0 ? (
                      <div className="space-y-1.5">
                        {askAnalytics.top_queries.map((q, idx) => {
                          const maxQ = askAnalytics.top_queries[0]?.count || 1;
                          const barWidth = Math.max((q.count / maxQ) * 100, 8);
                          return (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="text-xs text-gray-400 w-5 text-right shrink-0">{idx + 1}</span>
                              <div className="flex-1 min-w-0">
                                <div className="relative">
                                  <div
                                    className="absolute inset-y-0 left-0 bg-violet-100 rounded"
                                    style={{ width: `${barWidth}%` }}
                                  />
                                  <div className="relative flex items-center justify-between px-2 py-1">
                                    <span className="text-sm text-gray-800 truncate">{q.query}</span>
                                    <span className="text-xs text-gray-500 shrink-0 ml-2">{q.count}x</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">No search data yet</p>
                    )}
                  </div>

                  {/* Top users */}
                  {askAnalytics.top_users?.length > 0 && (
                    <div data-testid="ask-top-users">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="w-4 h-4 text-gray-500" />
                        <p className="text-sm font-medium text-gray-700">Most Active Users (Last 7 Days)</p>
                      </div>
                      <div className="space-y-1">
                        {askAnalytics.top_users.map((u, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-gray-50">
                            <span className="text-gray-700">
                              {u.name}
                              {u.indos_number && <span className="text-gray-400 ml-1">({u.indos_number})</span>}
                            </span>
                            <Badge variant="outline" className="text-xs font-mono">{u.count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  <p className="text-sm text-gray-400">Loading analytics...</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info */}
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-5 pb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">How Deep Search works</h4>
              <ul className="text-xs text-gray-600 space-y-1.5">
                <li>Users search the MARQ question bank first using semantic AI matching.</li>
                <li>If not satisfied, they click "Find Deeper" which calls the central AI service.</li>
                <li>The central AI service uses RAG (Retrieval-Augmented Generation) over PDFs to find answers.</li>
                <li>Each user has a daily limit to control API costs. The limit resets at midnight UTC.</li>
                <li>The API key is stored securely and never exposed to the frontend.</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AIServiceConfigPage;
